import type { MediaItem, MediaPage, Profile, UploadFileRequest, UploadAllocation } from '../../shared/types';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export const api = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiRequestError(body.error || `Request failed (${response.status})`, response.status);
  }

  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
};

export const createSession = (passphrase: string, profile: Profile) =>
  api<{ profile: Profile }>('/api/session', {
    method: 'POST',
    body: JSON.stringify({ passphrase, profile }),
  });

export const deleteSession = () => api<void>('/api/session', { method: 'DELETE' });

export const getMedia = (cursor?: string) =>
  api<MediaPage>(`/api/media${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);

export const allocateUploads = (files: UploadFileRequest[]) =>
  api<{ uploads: UploadAllocation[] }>('/api/uploads', {
    method: 'POST',
    body: JSON.stringify({ files }),
  });

export const completeUpload = (id: string) =>
  api<{ item: MediaItem }>(`/api/uploads/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
  });

export const deleteMedia = (id: string) =>
  api<void>(`/api/media/${encodeURIComponent(id)}`, { method: 'DELETE' });
